import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';
import { AlertService } from 'src/core/services/alert.service';
import { MedicineService } from 'src/core/services/medicine.service';
import { PermissionsService } from 'src/core/services/permissions.service';
import {
  collectPackagesMissingSageItemCode,
  MissingSagePackageRow,
} from 'src/core/utils/missing-sage-packages';
import { toPackageTypeApiPayload } from 'src/core/utils/package-type-payload';
import {
  SAGE_ITEM_CODE_HELPER,
  SAGE_ITEM_CODE_MISSING_WARNING,
  sageItemCodeForApi,
} from 'src/core/utils/sage-item-code';

@Component({
  selector: 'app-missing-sage-item-codes',
  templateUrl: './missing-sage-item-codes.component.html',
  styleUrls: ['./missing-sage-item-codes.component.scss'],
  standalone: false,
})
export class MissingSageItemCodesComponent implements OnInit, OnDestroy {
  readonly helper = SAGE_ITEM_CODE_HELPER;
  readonly missingWarning = SAGE_ITEM_CODE_MISSING_WARNING;

  displayedColumns: string[] = [
    'package_id',
    'medicine',
    'strength',
    'package_type',
    'label',
    'sage_item_code',
  ];

  rows: MissingSagePackageRow[] = [];
  visibleRows: MissingSagePackageRow[] = [];
  pagedRows: MissingSagePackageRow[] = [];
  searchInput = new UntypedFormControl('');
  pageIndex = 0;
  pageSize = 10;
  readonly pageSizeOptions = [10, 25, 50];
  loading = false;
  medicinesDone = 0;
  medicineTotal = 0;
  incomplete = false;
  savingPackageId: number | null = null;

  private _cancelled = false;
  private _draftCodes = new Map<number, string>();

  constructor(
    private _medicineService: MedicineService,
    private _alertService: AlertService,
    public readonly perm: PermissionsService
  ) {}

  ngOnInit(): void {
    this.searchInput.valueChanges.subscribe(() => this.applyFilter(true));
    this.load();
  }

  ngOnDestroy(): void {
    this._cancelled = true;
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.updatePage();
  }

  private applyFilter(resetPage = false): void {
    const query = String(this.searchInput.value ?? '').trim().toLowerCase();
    if (!query) {
      this.visibleRows = this.rows;
    } else {
      this.visibleRows = this.rows.filter((row) => {
        return (
          String(row.packageId).includes(query) ||
          row.medicineName.toLowerCase().includes(query) ||
          row.strength.toLowerCase().includes(query) ||
          row.packageType.toLowerCase().includes(query) ||
          row.label.toLowerCase().includes(query)
        );
      });
    }
    if (resetPage) this.pageIndex = 0;
    this.updatePage();
  }

  /** Show one page of the filtered packs. Typed codes stay in the draft map. */
  private updatePage(): void {
    const last = Math.max(0, Math.ceil(this.visibleRows.length / this.pageSize) - 1);
    if (this.pageIndex > last) this.pageIndex = last;
    const start = this.pageIndex * this.pageSize;
    this.pagedRows = this.visibleRows.slice(start, start + this.pageSize);
  }

  codeFor(row: MissingSagePackageRow): string {
    return this._draftCodes.get(row.packageId) ?? '';
  }

  onCodeInput(row: MissingSagePackageRow, value: string): void {
    this._draftCodes.set(row.packageId, value);
  }

  async load(): Promise<void> {
    this.loading = true;
    this.incomplete = false;
    this.medicinesDone = 0;
    this.medicineTotal = 0;
    this.rows = [];
    this.visibleRows = [];
    this.pagedRows = [];
    this.pageIndex = 0;

    try {
      const scan = await collectPackagesMissingSageItemCode({
        listMedicines: (page, limit) => this._medicineService.getMedicinesPage(page, limit),
        listDosageTypes: (medicineId, page, limit) =>
          this._medicineService.getMedicineDosageTypesPage(medicineId, page, limit),
        listPackages: (medicineId, dosageTypeId, page, limit) =>
          this._medicineService.getMedicineDosagePackageTypesPage(
            medicineId,
            dosageTypeId,
            page,
            limit
          ),
        onProgress: (state) => {
          if (this._cancelled) return;
          this.medicinesDone = state.medicinesDone;
          this.medicineTotal = state.medicineTotal;
        },
        isCancelled: () => this._cancelled,
      });

      if (this._cancelled) return;
      this.rows = scan.rows;
      this.applyFilter(true);
      this.medicinesDone = scan.medicinesDone;
      this.medicineTotal = scan.medicineTotal;
      this.incomplete = scan.incomplete;
    } finally {
      if (!this._cancelled) this.loading = false;
    }
  }

  async save(row: MissingSagePackageRow): Promise<void> {
    if (!this.perm.canEditMedicineCatalogue() || this.savingPackageId != null) return;

    const packageTypeId = row.pack.package_type?.id ?? row.pack.package_type_id;
    if (!packageTypeId) {
      this._alertService.warningAlert('This pack has no package type, so the code cannot be sent.');
      return;
    }

    const sageItemCode = sageItemCodeForApi(this.codeFor(row));
    const payload = toPackageTypeApiPayload({
      containedQuantity: row.pack.contained_quantity,
      packageType: { id: packageTypeId },
      labelName: row.pack.label_name,
      price: row.pack.price,
      unitsPerPack: row.pack.units_per_pack,
      minOrderQty: row.pack.min_order_qty,
      sageItemCode,
    });

    this.savingPackageId = row.packageId;
    try {
      const saved = await this._medicineService.editMedicineDosagePackageType(
        row.medicineId,
        row.medicineDosageTypeId,
        row.packageId,
        payload
      );
      if (!saved || sageItemCode === '') return;
      this._draftCodes.delete(row.packageId);
      this.rows = this.rows.filter((item) => item.packageId !== row.packageId);
      this.applyFilter();
    } finally {
      this.savingPackageId = null;
    }
  }
}
